// Presentation-only geometry. Inputs retain their canonical units; no weather
// aggregation or mutation belongs here.
export type ValueRange = readonly [number, number];
// Current position within today's forecast range. Observed extrema are metadata,
// not the fill value: an earlier high must not keep the bar full all day.
export function conditionProgress(
  current: number | null,
  _observed: ValueRange | null,
  forecast: ValueRange | null,
  scale: ValueRange,
) {
  const expected = forecast && measurementRange(...forecast);
  const [low, high] = expected && expected[1] > expected[0] ? expected : scale;
  const value = finiteMeasurement(current);
  return {
    low,
    high,
    fill: value === null || high <= low ? 0
      : Math.max(0, Math.min(100, ((value - low) / (high - low)) * 100)),
  };
}
export function finiteMeasurement(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isFinite(value) &&
    ![-999, -999.99, Number.MIN_SAFE_INTEGER].includes(value)
    ? value
    : null;
}
export function measurementRange(
  low: unknown,
  high: unknown,
): ValueRange | null {
  const a = finiteMeasurement(low),
    b = finiteMeasurement(high);
  return a !== null && b !== null && a <= b ? [a, b] : null;
}
export function conditionBar(
  current: number | null,
  observed: ValueRange | null,
  forecast: ValueRange | null,
  scale: ValueRange,
) {
  const validObserved = observed && measurementRange(...observed);
  const validForecast = forecast && measurementRange(...forecast);
  const value = finiteMeasurement(current);
  const values = [
    ...(validObserved ?? []),
    ...(validForecast ?? []),
    ...(value === null ? [] : [value]),
  ];
  const low = Math.min(scale[0], ...values),
    high = Math.max(scale[1], ...values);
  const position = (n: number) =>
    Math.max(0, Math.min(100, ((n - low) / (high - low || 1)) * 100));
  const segment = (range: ValueRange) => ({
    left: position(range[0]),
    width: position(range[1]) - position(range[0]),
  });
  return {
    low,
    high,
    forecast: validForecast ? segment(validForecast) : null,
    fill: validObserved
      ? segment(validObserved)
      : value !== null
        ? segment([low, value])
        : null,
    marker: value === null ? null : position(value),
    hasObservedRange: !!validObserved,
  };
}
